import React, { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Search, Loader2 } from "lucide-react";
import TabFreshnessNote from "./TabFreshnessNote";

export default function SerpTab({ client }) {
  const [keyword, setKeyword] = useState(client.keywords?.[0] || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (kw) => {
    const k = (kw || keyword).trim();
    if (!k) return;
    setKeyword(k);
    setLoading(true);
    try {
      const { data } = await api.post("/seo/serp", {
        keyword: k,
        location_name: client.location_name,
        language_name: client.language_name,
      });
      setData(data.result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SERP fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const items = (data?.items || []).filter((i) => i.type === "organic").slice(0, 20);
  const domainLower = client.domain.toLowerCase();

  return (
    <div className="space-y-4" data-testid="serp-tab">
      <TabFreshnessNote testid="serp-freshness">
        Manual on-demand only — Google SERP fetched live each time you click Analyze · Results are not stored.
      </TabFreshnessNote>

      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
          SERP analysis
        </div>
        <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">Top 20 organic results</h3>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="h-3.5 w-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-[#a0aec0]" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            data-testid="serp-keyword-input"
            placeholder="Enter a keyword…"
            className="w-full bg-white border border-[#e2e8f0] rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !keyword || client.frozen}
          title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
          data-testid="serp-run"
          className="btn-primary inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Analyze
        </button>
      </form>

      {client.keywords?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {client.keywords.slice(0, 10).map((kw) => (
            <button
              key={kw}
              onClick={() => run(kw)}
              className="text-xs px-3 py-1.5 bg-white border border-[#e2e8f0] rounded-full hover:border-[#4fd1c5] hover:text-[#2c7a7b] transition-colors"
            >
              {kw}
            </button>
          ))}
        </div>
      )}

      {data && (
        <div className="card-soft overflow-hidden">
          <div className="px-5 py-4 border-b border-[#edf2f7] flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-semibold">
                results for
              </div>
              <h4 className="font-display text-base mt-0.5 text-[#1a202c]">{data.keyword}</h4>
            </div>
            <div className="text-xs text-[#718096] font-mono-tight">
              {data.se_results_count?.toLocaleString()} results
            </div>
          </div>
          <div className="divide-y divide-[#edf2f7] max-h-[640px] overflow-y-auto">
            {items.length === 0 && (
              <div className="p-8 text-center text-sm text-[#a0aec0]">No organic results.</div>
            )}
            {items.map((it, i) => {
              const isClient = (it.domain || "").toLowerCase().includes(domainLower);
              return (
                <div
                  key={i}
                  data-testid={`serp-row-${i}`}
                  className={`px-5 py-4 hover:bg-[#f7fafc] ${isClient ? "bg-[#4fd1c5]/8" : ""}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 shrink-0 font-mono-tight text-[#a0aec0] text-sm">{it.rank_group}.</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-mono-tight text-[#718096] truncate">
                        {((it.url_from_breadcrumb && it.breadcrumb) || it.url || "").replace(/^https?:\/\//, "").slice(0, 90)}
                      </div>
                      <a href={it.goto_url || it.url} target="_blank" rel="noreferrer" className="text-sm font-semibold hover:text-[#2c7a7b] text-[#1a202c] block mt-0.5">
                        {it.title}
                      </a>
                      <div className="text-xs text-[#4a5568] mt-1 line-clamp-2">{it.description}</div>
                    </div>
                    {isClient && (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-brand text-white font-bold font-mono-tight shadow-sm">
                        YOU
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!data && !loading && (
        <div className="text-sm text-[#a0aec0] font-mono-tight">
          Enter a keyword above to run a live SERP analysis.
        </div>
      )}
    </div>
  );
}
